/**
 * useEmploymentTypes Hook
 *
 * React hook for fetching and managing employment types.
 * Automatically fetches types based on business country.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEmploymentTypesForBusiness,
  getEmploymentTypesByCountry,
  getEmploymentTypeById,
  toEmploymentTypeOptions,
  type EmploymentType,
  type EmploymentTypeOption,
} from '@/lib/compliance';

// =============================================================================
// Query Keys
// =============================================================================

export const employmentTypesKeys = {
  all: ['employment-types'] as const,
  byCountry: (countryCode: string) =>
    [...employmentTypesKeys.all, 'country', countryCode] as const,
  byBusiness: (businessId: string) =>
    [...employmentTypesKeys.all, 'business', businessId] as const,
  byId: (id: string) => [...employmentTypesKeys.all, 'id', id] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch employment types for a business
 *
 * @param businessId - The business ID to fetch types for
 * @param options - Query options
 */
export function useEmploymentTypes(
  businessId: string | undefined,
  options?: {
    enabled?: boolean;
    locale?: 'native' | 'en';
  }
) {
  const { enabled = true, locale = 'native' } = options || {};

  const query = useQuery({
    queryKey: employmentTypesKeys.byBusiness(businessId || ''),
    queryFn: () => getEmploymentTypesForBusiness(businessId!),
    enabled: enabled && !!businessId,
    staleTime: 1000 * 60 * 60, // 1 hour - employment types rarely change
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  return {
    ...query,
    employmentTypes: query.data || [],
    options: query.data ? toEmploymentTypeOptions(query.data, locale) : [],
  };
}

/**
 * Fetch employment types by country code
 *
 * @param countryCode - ISO country code (e.g., "SK", "DE")
 * @param options - Query options
 */
export function useEmploymentTypesByCountry(
  countryCode: string | undefined,
  options?: {
    enabled?: boolean;
    locale?: 'native' | 'en';
  }
) {
  const { enabled = true, locale = 'native' } = options || {};

  const query = useQuery({
    queryKey: employmentTypesKeys.byCountry(countryCode || ''),
    queryFn: () => getEmploymentTypesByCountry(countryCode!),
    enabled: enabled && !!countryCode,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  return {
    ...query,
    employmentTypes: query.data || [],
    options: query.data ? toEmploymentTypeOptions(query.data, locale) : [],
  };
}

/**
 * Fetch a single employment type by ID
 *
 * @param id - Employment type ID
 * @param options - Query options
 */
export function useEmploymentType(
  id: string | undefined | null,
  options?: {
    enabled?: boolean;
  }
) {
  const { enabled = true } = options || {};

  return useQuery({
    queryKey: employmentTypesKeys.byId(id || ''),
    queryFn: () => getEmploymentTypeById(id!),
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

/**
 * Hook to invalidate employment types cache
 */
export function useInvalidateEmploymentTypes() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () =>
      queryClient.invalidateQueries({ queryKey: employmentTypesKeys.all }),
    invalidateByCountry: (countryCode: string) =>
      queryClient.invalidateQueries({
        queryKey: employmentTypesKeys.byCountry(countryCode),
      }),
    invalidateByBusiness: (businessId: string) =>
      queryClient.invalidateQueries({
        queryKey: employmentTypesKeys.byBusiness(businessId),
      }),
  };
}

// =============================================================================
// Utility Hooks
// =============================================================================

/**
 * Get employment type options formatted for select components
 */
export function useEmploymentTypeOptions(
  businessId: string | undefined,
  locale: 'native' | 'en' = 'native'
): {
  options: EmploymentTypeOption[];
  isLoading: boolean;
  error: Error | null;
} {
  const { options, isLoading, error } = useEmploymentTypes(businessId, { locale });

  return {
    options,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Find employment type from cache by code
 */
export function useFindEmploymentTypeByCode(
  businessId: string | undefined,
  code: string | undefined
): EmploymentType | undefined {
  const { employmentTypes } = useEmploymentTypes(businessId, { enabled: !!code });

  if (!code || !employmentTypes.length) return undefined;

  return employmentTypes.find((type) => type.code === code);
}
